import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { api } from "../api/api";
import { identifyUser, resetUser } from "../analytics";

const AuthContext = createContext({
  userToken: null,
  isAdmin: false,
  isAuthReady: false,
  isLoggedIn: false,
  userStatus: "not_certified",
  usedStamps: 0,
  totalStamps: 0,
  userName: "",
  login: () => {},
  logout: () => {},
  updateHandler: () => {},
  clearAdmin: () => {},
});

export const AuthContextProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    localStorage.getItem("bbr_authLogin") === "true" || false
  );
  const token = localStorage.getItem("bbr_authUserToken");

  const [userToken, setUserToken] = useState(token);
  // Never trust localStorage alone for admin — verified via API on load
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(!token);
  const [usedStamps, setUsedStamps] = useState(
    parseInt(localStorage.getItem("bbr_usedStamps"), 10) || 0
  );
  const [totalStamps, setTotalStamps] = useState(
    parseInt(localStorage.getItem("bbr_totalStamps"), 10) || 0
  );
  const [userStatus, setUserStatus] = useState(
    localStorage.getItem("bbr_userStatus") || "not_certified"
  );
  const [userName, setUserName] = useState(
    localStorage.getItem("bbr_userName") || ""
  );

  useEffect(() => {
    if (userToken) {
      api.defaults.headers.common["Authorization"] = `Bearer ${userToken}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [userToken]);

  const loginHandler = useCallback(
    (token, role, status, totalStampsValue, usedStampsValue, user_info, identity = {}) => {
      const fullName = `${user_info?.firstName || ""} ${user_info?.lastName || ""}`.trim();

      localStorage.setItem("bbr_authLogin", "true");
      localStorage.setItem("bbr_authUserToken", token);
      localStorage.setItem("bbr_userStatus", status);
      localStorage.setItem("bbr_totalStamps", totalStampsValue);
      localStorage.setItem("bbr_usedStamps", usedStampsValue);
      localStorage.setItem("bbr_userName", fullName);

      if (role === "admin") {
        localStorage.setItem("bbr_isAdmin", "true");
        setIsAdmin(true);
      } else {
        localStorage.setItem("bbr_isAdmin", "false");
        setIsAdmin(false);
      }

      setIsLoggedIn(true);
      setUserToken(token);
      setUserStatus(status);
      setTotalStamps(totalStampsValue);
      setUsedStamps(usedStampsValue);
      setUserName(fullName);

      // Identify the user in PostHog after a successful login / session restore.
      identifyUser({
        id: identity.id || identity._id || user_info?._id,
        email: identity.email || user_info?.email,
        name: fullName || identity.name,
        userStatus: status,
        userRole: role,
      });
    },
    []
  );

  const clearAdmin = useCallback(() => {
    localStorage.removeItem("bbr_isAdmin");
    setIsAdmin(false);
  }, []);

  const logoutHandler = useCallback(() => {
    localStorage.removeItem("bbr_authLogin");
    localStorage.removeItem("bbr_authUserToken");
    localStorage.removeItem("bbr_isAdmin");
    localStorage.removeItem("bbr_userStatus");
    localStorage.removeItem("bbr_totalStamps");
    localStorage.removeItem("bbr_usedStamps");
    localStorage.removeItem("bbr_userName");

    delete api.defaults.headers.common["Authorization"];

    setIsAdmin(false);
    setIsLoggedIn(false);
    setUserToken(null);
    setUserStatus("not_certified");
    setUsedStamps(0);
    setTotalStamps(0);
    setUserName("");
    setIsAuthReady(true);

    // Clear PostHog person state so the next visitor is anonymous.
    resetUser();
  }, []);

  const updateHandler = useCallback(() => {
    const currentToken = localStorage.getItem("bbr_authUserToken");
    if (!currentToken) return Promise.resolve();

    return api
      .get("auth/getUser", {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((data) => {
        const user = data.data.user;
        loginHandler(
          currentToken,
          user.userRole,
          user.userStatus,
          user.tokens,
          user.usedTokens,
          user.user_info,
          { id: user._id, email: user.email }
        );
        return user;
      })
      .catch((err) => {
        console.error("Error updating user", err);
        throw err;
      });
  }, [loginHandler]);

  // Verify session (including admin role) via API once on load — do not trust localStorage for isAdmin
  useEffect(() => {
    const currentToken = localStorage.getItem("bbr_authUserToken");
    if (!currentToken) {
      setIsAdmin(false);
      setIsAuthReady(true);
      return;
    }

    let cancelled = false;

    api
      .get("auth/getUser", {
        headers: { Authorization: `Bearer ${currentToken}` },
      })
      .then((data) => {
        if (cancelled) return;
        const user = data.data.user;
        loginHandler(
          currentToken,
          user.userRole,
          user.userStatus,
          user.tokens,
          user.usedTokens,
          user.user_info,
          { id: user._id, email: user.email }
        );
      })
      .catch((err) => {
        console.error("Error verifying session", err);
        if (!cancelled) {
          setIsAdmin(false);
          localStorage.removeItem("bbr_isAdmin");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAuthReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      userToken,
      isLoggedIn,
      isAdmin,
      isAuthReady,
      userStatus,
      usedStamps,
      totalStamps,
      userName,
      login: loginHandler,
      logout: logoutHandler,
      updateHandler,
      clearAdmin,
    }),
    [
      userToken,
      isLoggedIn,
      isAdmin,
      isAuthReady,
      userStatus,
      usedStamps,
      totalStamps,
      userName,
      loginHandler,
      logoutHandler,
      updateHandler,
      clearAdmin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
